// src/utils/researchApi.js
// ========================================
// Research API Helper Functions
// ========================================
// Handles all API calls to the research endpoints with authentication

const API_BASE = 'http://localhost:5001/api/research';

// 🔧 Helper: Get token from localStorage
const getAuthToken = () => {
    const token = localStorage.getItem('token');
    if (!token) {
        console.warn('⚠️ No auth token found in localStorage');
    }
    return token;
};

// 🔧 Helper: Create headers with auth token
const getAuthHeaders = () => {
    const token = getAuthToken();
    if (!token) {
        throw new Error('Not authenticated. Please log in.');
    }
    return {
        'Authorization': `Bearer ${token}`
    };
};

// ========================================
// PUBLIC ENDPOINTS (No Auth Required)
// ========================================

/**
 * Get all published research (for homepage/public view)
 * @returns {Promise<Array>} Array of published research items
 */
export const getPublicResearch = async () => {
    try {
        console.log('📡 Fetching public research...');
        const response = await fetch(`${API_BASE}/public`, {
            method: 'GET'
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        console.log('✅ Fetched public research:', data.length, 'items');
        return data;
    } catch (error) {
        console.error('❌ Failed to fetch public research:', error);
        throw error;
    }
};

// ========================================
// ADMIN ENDPOINTS (Auth Required)
// ========================================

/**
 * Get all research (admin view - includes unpublished)
 * @returns {Promise<Array>} Array of all research items
 */
export const getAllResearch = async () => {
    try {
        console.log('📡 Fetching all research (admin)...');
        const response = await fetch(API_BASE, {
            method: 'GET',
            headers: getAuthHeaders()
        });

        if (response.status === 401) {
            throw new Error('Session expired. Please log in again.');
        }

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        console.log('✅ Fetched all research:', data.length, 'items');
        return data;
    } catch (error) {
        console.error('❌ Failed to fetch research:', error);
        throw error;
    }
};

/**
 * Add new research (admin only)
 * @param {FormData} formData - Form data with research fields and optional file
 * @returns {Promise<Object>} Response with success message
 */
export const addResearch = async (formData) => {
    try {
        console.log('📡 Adding new research...');

        const token = getAuthToken();
        if (!token) {
            throw new Error('Not authenticated. Please log in.');
        }

        const response = await fetch(API_BASE, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`
                // ⚠️ IMPORTANT: Do NOT set Content-Type for FormData!
                // Browser sets it automatically with correct boundary
            },
            body: formData
        });

        if (response.status === 401) {
            throw new Error('Session expired. Please log in again.');
        }

        if (response.status === 403) {
            throw new Error('Admin access required.');
        }

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        console.log('✅ Research added successfully:', data);
        return data;
    } catch (error) {
        console.error('❌ Failed to add research:', error);
        throw error;
    }
};

/**
 * Update existing research (admin only)
 * @param {number|string} id - Research ID
 * @param {FormData} formData - Form data with updated fields
 * @returns {Promise<Object>} Response with success message
 */
export const updateResearch = async (id, formData) => {
    try {
        console.log(`📡 Updating research ID: ${id}...`);

        const token = getAuthToken();
        if (!token) {
            throw new Error('Not authenticated. Please log in.');
        }

        const response = await fetch(`${API_BASE}/${id}`, {
            method: 'PUT',
            headers: {
                'Authorization': `Bearer ${token}`
                // ⚠️ IMPORTANT: Do NOT set Content-Type for FormData!
            },
            body: formData
        });

        if (response.status === 401) {
            throw new Error('Session expired. Please log in again.');
        }

        if (response.status === 403) {
            throw new Error('Admin access required.');
        }

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        console.log('✅ Research updated successfully:', data);
        return data;
    } catch (error) {
        console.error('❌ Failed to update research:', error);
        throw error;
    }
};

/**
 * Delete research (admin only)
 * @param {number|string} id - Research ID to delete
 * @returns {Promise<Object>} Response with success message
 */
export const deleteResearch = async (id) => {
    try {
        console.log(`📡 Deleting research ID: ${id}...`);

        const token = getAuthToken();
        if (!token) {
            throw new Error('Not authenticated. Please log in.');
        }

        const response = await fetch(`${API_BASE}/${id}`, {
            method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            }
        });

        if (response.status === 401) {
            throw new Error('Session expired. Please log in again.');
        }

        if (response.status === 403) {
            throw new Error('Admin access required.');
        }

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        console.log('✅ Research deleted successfully:', data);
        return data;
    } catch (error) {
        console.error('❌ Failed to delete research:', error);
        throw error;
    }
};

// ========================================
// HELPER: Check if user is authenticated
// ========================================

/**
 * Check if user has a valid token
 * @returns {boolean} True if token exists
 */
export const isAuthenticated = () => {
    return !!getAuthToken();
};

/**
 * Clear authentication (logout)
 */
export const clearAuth = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    console.log('Logged out - auth cleared');
};